import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {ListResult, SingleResult} from 'utils/types';

export interface DealInfo {
  tickets: {
    [id: string]: {
      id: string;
      properties: {
        subject: string;
        hs_pipeline: string;
      };
    };
  };
  updated_at: string;
  quote_count: number;
  published_quote_count: number;
  invoice_count: number;
  published_invoice_count: number;
  primary_company_hubspot_id: string | null;
  primary_company_pricing_details: string | null;
}

interface HubSpotObjectDifference {
  campaign_value: any;
  hubspot_value: any;
}
export interface PushToHubspotStatus {
  id: number;
  campaign: number;
  last_push_to_hubspot_at: string | null;
  last_push_to_hubspot_success: boolean | null;
  last_push_to_hubspot_errors: string[] | null;
  last_check_for_differences_at: string | null;
  deal_differences: Record<string, HubSpotObjectDifference> | null;
  ticket_differences: Record<string, HubSpotObjectDifference> | null;
}

export interface CampaignWithDifferences {
  id: number;
  campaign_name: string;
  path_id: string;
  hubspot_deal_id: string;
  deal_differences: Record<string, HubSpotObjectDifference>;
  ticket_differences: Record<string, HubSpotObjectDifference>;
  last_check_for_differences_at: string | null;
}

interface IState {
  hubspotDealInfo: SingleResult<DealInfo>;
  hubspotPushStatus: SingleResult<PushToHubspotStatus>;
  campaignsWithDifferences: ListResult<CampaignWithDifferences>;
}

const handleApiError = (error: any, rejectWithValue: (value: unknown) => unknown) => {
  if (error?.response?.data) {
    return rejectWithValue(error.response.data);
  }
  if (error?.message) {
    return rejectWithValue({detail: error.message});
  }
  return rejectWithValue({detail: 'Unexpected error contacting HubSpot API.'});
};

export const hubspotEndpoints = {
  getDealInfo: createAsyncThunk(
    'hubspot/fetchDealInfo',
    async ({campaignId}: {campaignId: number}, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/hubspot/campaigns/${campaignId}/`);
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
  createQuote: createAsyncThunk(
    'hubspot/createQuote',
    async ({campaignId}: {campaignId: number}, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/hubspot/campaigns/${campaignId}/create_quote/`);
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
  createInvoice: createAsyncThunk(
    'hubspot/createInvoice',
    async ({campaignId}: {campaignId: number}, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/hubspot/campaigns/${campaignId}/create_invoice/`);
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
  markDataLoadingTasksAsCompleted: createAsyncThunk(
    'hubspot/markDataLoadingTasksAsCompleted',
    async ({campaignId}: {campaignId: number}, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(
          `/hubspot/campaigns/${campaignId}/mark_data_loading_tasks_as_completed/`,
        );
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
  getPushStatus: createAsyncThunk(
    'hubspot/fetchPushStatus',
    async ({campaignId}: {campaignId: number}, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/hubspot/campaign_push_to_hubspot_status/${campaignId}/`);
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
  getCampaignsWithDifferences: createAsyncThunk(
    'hubspot/fetchCampaignsWithDifferences',
    async ({search}: {search?: string}, {rejectWithValue}) => {
      try {
        const params = search ? {search} : {};
        const response = await ApiService.get('/hubspot/campaigns_with_differences/', {params});
        return response.data;
      } catch (error: any) {
        return handleApiError(error, rejectWithValue);
      }
    },
  ),
};

const initialState = {
  hubspotDealInfo: {as: 'idle'},
  hubspotPushStatus: {as: 'idle'},
  campaignsWithDifferences: {as: 'idle'},
};

export const hubspotSlice = createSlice({
  name: 'hubspot',
  initialState: {
    ...initialState,
  } as IState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, hubspotEndpoints.getDealInfo, 'hubspotDealInfo');
    addResultCases(builder, hubspotEndpoints.getPushStatus, 'hubspotPushStatus');
    addResultCases(builder, hubspotEndpoints.getCampaignsWithDifferences, 'campaignsWithDifferences');
  },
});

export const hubspotActions = hubspotSlice.actions;

export type HubspotSlice = {
  [hubspotSlice.name]: ReturnType<(typeof hubspotSlice)['reducer']>;
};
